import React, { createContext, useContext, useRef, useState } from "react";
import {
  ContentBlock,
  ContentState,
  EditorState,
  SelectionState,
} from "draft-js";

import { resizeFile } from "views/apps/paciente/ficha/historico/imageUpload/imageUploadHelpers";

import "./ImageBlock.css";

interface IBlockRenderConfig {
  isReadOnly: () => boolean;
  isImageAlignmentEnabled: () => boolean;
  getEditorState: () => EditorState;
  onChange: (editorState: EditorState) => void;
}

interface IImageBlock {
  block: ContentBlock;
  contentState: ContentState;
  blockProps: IBlockRenderConfig;
}

type Alignment = "left" | "center" | "right";

export interface ICropRequest {
  entityKey: string;
  src: string;
  /** Largura exibida no editor no momento do pedido, em px. */
  displayWidth: number;
}

/**
 * Canal entre o bloco da imagem e o editor. O bloco é renderizado pelo
 * draft-js (fora do alcance das props do editor), então o pedido de recorte
 * sobe por contexto; o modal fica fora do editor para que o draft-js não
 * intercepte os eventos de mouse/teclado dele.
 *
 * `onChange` é o onEditorStateChange do editor: o onChange interno do
 * react-draft-wysiwyg descarta qualquer estado cuja seleção esteja num bloco
 * atômico (ex: remover uma imagem colada em outra), então as ações da imagem
 * não passam por ele.
 */
export const ImageBlockContext = createContext<{
  onCropRequest?: (request: ICropRequest) => void;
  onChange?: (editorState: EditorState) => void;
}>({});

const MIN_IMAGE_WIDTH = 60;

const FULL_WIDTH = "100%";

// Só números/px viram número: "100%" ou "auto" seguem como string.
const parsePx = (value?: string | number) => {
  if (typeof value === "string" && !/^\s*[\d.]+\s*(px)?\s*$/.test(value))
    return undefined;
  const parsed = parseFloat(String(value ?? ""));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

/**
 * Atualiza os dados da entidade da imagem. O mergeEntityData do draft-js
 * muta a entidade e devolve a MESMA instância de ContentState, o que faz quem
 * compara referências (ex: gravação de rascunho do histórico) achar que nada
 * mudou; por isso o conteúdo é recriado.
 */
export const updateImageData = (
  editorState: EditorState,
  entityKey: string,
  data: Record<string, unknown>
) => {
  const content = editorState
    .getCurrentContent()
    .mergeEntityData(entityKey, data);
  const newContent = ContentState.createFromBlockArray(
    content.getBlocksAsArray(),
    content.getEntityMap()
  ).merge({
    selectionBefore: editorState.getSelection(),
    selectionAfter: editorState.getSelection(),
  }) as ContentState;

  return EditorState.push(editorState, newContent, "change-block-data");
};

/** Remove o bloco atômico da imagem, levando o cursor ao bloco vizinho. */
const removeImageBlock = (editorState: EditorState, blockKey: string) => {
  const content = editorState.getCurrentContent();
  const neighbor =
    content.getBlockAfter(blockKey) || content.getBlockBefore(blockKey);

  if (!neighbor) {
    return EditorState.push(
      editorState,
      ContentState.createFromText(""),
      "remove-range"
    );
  }

  const selection = SelectionState.createEmpty(neighbor.getKey());
  const newContent = content.merge({
    blockMap: content.getBlockMap().delete(blockKey),
    selectionBefore: editorState.getSelection(),
    selectionAfter: selection,
  }) as ContentState;

  return EditorState.forceSelection(
    EditorState.push(editorState, newContent, "remove-range"),
    selection
  );
};

const ImageBlock: React.FC<IImageBlock> = ({
  block,
  contentState,
  blockProps,
}) => {
  const [hovered, setHovered] = useState(false);
  const { onCropRequest, onChange: onEditorChange } =
    useContext(ImageBlockContext);
  // Tamanho exibido durante o arraste; só vai para a entidade ao soltar, para
  // não gerar uma entrada de desfazer (e um rascunho) a cada pixel.
  const [resizing, setResizing] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<{
    startX: number;
    startWidth: number;
    ratio: number;
    maxWidth: number;
    direction: number;
  } | null>(null);
  const { isReadOnly, isImageAlignmentEnabled, getEditorState } = blockProps;
  const onChange = onEditorChange ?? blockProps.onChange;

  const entityKey = block.getEntityAt(0);
  const { src, alignment, height, width, alt } = contentState
    .getEntity(entityKey)
    .getData();
  const editable = !isReadOnly();
  const isFullWidth = width === FULL_WIDTH;

  const setAlignment = (value: Alignment) => {
    onChange(
      updateImageData(getEditorState(), entityKey, { alignment: value })
    );
    setHovered(false);
  };

  const requestCrop = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setHovered(false);
    onCropRequest?.({
      entityKey,
      src,
      displayWidth: imgRef.current?.offsetWidth || parsePx(width) || 0,
    });
  };

  // Largura em % (e não em px): ocupa toda a largura tanto no editor quanto
  // na exibição do post, que tem outra largura. Clicar de novo volta ao
  // tamanho padrão de inserção.
  const toggleFullWidth = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const img = imgRef.current;

    if (!isFullWidth) {
      onChange(
        updateImageData(getEditorState(), entityKey, {
          width: FULL_WIDTH,
          height: "auto",
        })
      );
      return;
    }

    const [defaultWidth, defaultHeight] = resizeFile(
      img?.naturalWidth,
      img?.naturalHeight
    );
    onChange(
      updateImageData(getEditorState(), entityKey, {
        width: defaultWidth ?? "auto",
        height: defaultHeight ?? "auto",
      })
    );
  };

  const remove = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onChange(removeImageBlock(getEditorState(), block.getKey()));
  };

  const onResizeStart = (event: React.PointerEvent<HTMLSpanElement>) => {
    const img = imgRef.current;
    if (!img) return;
    event.preventDefault();
    event.stopPropagation();

    const startWidth = img.offsetWidth;
    // Proporção original do arquivo: evita herdar uma distorção de tamanhos
    // salvos antes (ex: largura e altura que não batem com a imagem).
    const ratio =
      img.naturalWidth && img.naturalHeight
        ? img.naturalWidth / img.naturalHeight
        : startWidth / (img.offsetHeight || 1);
    const editorRoot = img.closest(".DraftEditor-root") as HTMLElement | null;

    dragRef.current = {
      startX: event.clientX,
      startWidth,
      ratio,
      maxWidth: editorRoot?.clientWidth || Infinity,
      // Centralizada, a imagem cresce para os dois lados: dobra o delta para
      // o canto acompanhar o ponteiro. À direita, a alça fica no canto
      // esquerdo e a imagem cresce para a esquerda.
      direction: alignment === "left" ? 1 : alignment === "right" ? -1 : 2,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setResizing({ width: startWidth, height: startWidth / ratio });
  };

  const onResizeMove = (event: React.PointerEvent<HTMLSpanElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const delta = (event.clientX - drag.startX) * drag.direction;
    const width = Math.round(
      Math.min(
        drag.maxWidth,
        Math.max(MIN_IMAGE_WIDTH, drag.startWidth + delta)
      )
    );
    setResizing({ width, height: Math.round(width / drag.ratio) });
  };

  const onResizeEnd = () => {
    if (!dragRef.current || !resizing) return;
    dragRef.current = null;
    setResizing(null);

    onChange(
      updateImageData(getEditorState(), entityKey, {
        width: `${resizing.width}px`,
        height: `${resizing.height}px`,
      })
    );
  };

  const imageStyle = resizing
    ? { width: resizing.width, height: resizing.height }
    : {
        width: parsePx(width) ?? width,
        height: parsePx(height) ?? height,
      };

  return (
    <span
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={
        (resizing ? "image-block-resizing " : "") +
        "rdw-image-alignment " +
        (alignment === "left"
          ? "rdw-image-left"
          : alignment === "right"
          ? "rdw-image-right"
          : "rdw-image-center")
      }
    >
      <span
        className={
          "rdw-image-imagewrapper image-block-wrapper" +
          (isFullWidth && !resizing ? " image-block-full" : "")
        }
      >
        <img ref={imgRef} src={src} alt={alt} style={imageStyle} />
        {editable && (
          <span
            className={
              "image-block-resize-handle" +
              (alignment === "right" ? " image-block-resize-handle-left" : "")
            }
            title="Arraste para redimensionar"
            onPointerDown={onResizeStart}
            onPointerMove={onResizeMove}
            onPointerUp={onResizeEnd}
            onPointerCancel={onResizeEnd}
          />
        )}
        {resizing && (
          <span className="image-block-size-badge">
            {resizing.width} × {resizing.height}
          </span>
        )}
        {editable && (
          <span className="image-block-actions">
            <button
              type="button"
              title={
                isFullWidth
                  ? "Voltar ao tamanho padrão"
                  : "Ocupar toda a largura"
              }
              aria-label={
                isFullWidth
                  ? "Voltar ao tamanho padrão"
                  : "Ocupar toda a largura"
              }
              onMouseDown={toggleFullWidth}
              className={"image-block-action" + (isFullWidth ? " active" : "")}
            >
              <i
                className={
                  isFullWidth
                    ? "mdi mdi-arrow-collapse-horizontal"
                    : "mdi mdi-arrow-expand-horizontal"
                }
              />
            </button>
            {onCropRequest && (
              <button
                type="button"
                title="Recortar imagem"
                aria-label="Recortar imagem"
                onMouseDown={requestCrop}
                className="image-block-action"
              >
                <i className="mdi mdi-crop" />
              </button>
            )}
            <button
              type="button"
              title="Remover imagem"
              aria-label="Remover imagem"
              onMouseDown={remove}
              className="image-block-action image-block-action-danger"
            >
              <i className="mdi mdi-close" />
            </button>
          </span>
        )}
        {editable && hovered && !resizing && isImageAlignmentEnabled() && (
          <div
            className={
              "rdw-image-alignment-options-popup" +
              (alignment === "right"
                ? " rdw-image-alignment-options-popup-right"
                : "")
            }
          >
            {(["left", "center", "right"] as Alignment[]).map((value) => (
              <span
                key={value}
                className="rdw-option-wrapper rdw-image-alignment-option"
                onClick={() => setAlignment(value)}
              >
                {value[0].toUpperCase()}
              </span>
            ))}
          </div>
        )}
      </span>
    </span>
  );
};

/**
 * Substitui o renderizador de imagem do react-draft-wysiwyg por um com botão
 * de remoção. A lib captura essa função uma única vez (no constructor), por
 * isso ela não pode depender de props: usa o getEditorState/onChange da lib.
 */
export const imageBlockRenderFunc = (
  block: ContentBlock,
  config: IBlockRenderConfig
) => {
  if (block.getType() !== "atomic") return undefined;
  const entityKey = block.getEntityAt(0);
  if (!entityKey) return undefined;

  const content = config.getEditorState().getCurrentContent();
  if (content.getEntity(entityKey).getType() !== "IMAGE") return undefined;

  return { component: ImageBlock, editable: false, props: config };
};

export default ImageBlock;
